<template>
  <field
    :empty="isEmpty && !forceLabelToTop && !placeholder"
    v-bind="$attrs"
    :label="label"
    :hide-label="hideLabel"
    :icon="icon"
    :disabled="disabled"
    class="text-left"
  >
    <template #label>
      <slot name="label">
        {{ label }}
      </slot>
    </template>
    <input
      v-model="internalValue"
      v-bind="{ ...$attrs, class: { 'has-icon': !!$slots.icon, 'appearance-clean': cleanAppearance } }"
      :placeholder="placeholder"
      :aria-label="label"
      :disabled="disabled"
      :type="type ?? 'text'"
      v-on="inputEvents"
    >
    <template v-if="$slots.icon" #icon>
      <slot name="icon" />
    </template>
  </field>
</template>

<script lang="ts" setup>
import {
  watch, computed, ref, type FunctionalComponent,
} from 'vue';
import { useVModel, type VueInstance } from '@vueuse/core';
import Field from '@components/utils/inputs/Field.vue';

interface Props {
  modelValue?: string | number;
  label?: string;
  placeholder?: string;
  pattern?: RegExp;
  icon?: VueInstance | FunctionalComponent | string | object;
  forceLabelToTop?: boolean;
  hideLabel?: boolean;
  inputEvents?: object;
  type?: string;
  disabled?: boolean;
  cleanAppearance?: boolean;
}

const emit = defineEmits(['update:modelValue']);
const props = defineProps<Props>();
const value = useVModel(props, 'modelValue', emit, { passive: true });

const internalValue = ref<string | number | undefined>(value.value);

watch(value, (newExternalValue) => {
  if (internalValue.value !== newExternalValue) {
    internalValue.value = newExternalValue;
  }
});

watch(internalValue, (newValue) => {
  if (props.pattern && typeof newValue === 'string') {
    internalValue.value = newValue.match(props.pattern)?.[0] ?? '';
  } else if (props.type === 'number' && newValue === '') {
    internalValue.value = undefined;
  } else {
    internalValue.value = newValue;
  }
  value.value = internalValue.value;
}, { flush: 'post' });

const isEmpty = computed(() => {
  const currentValue = value.value;
  if (typeof currentValue === 'number') return !currentValue && currentValue !== 0;
  return !currentValue;
});
</script>
<style lang="scss" scoped>
input {
  background-color: var(--input-bg, var(--color-background-light));
  padding: var(--input-padding, #{px(10)} #{px(14)});
  outline: 0;

  &:disabled {
    @apply border-light-gray;
    @apply bg-table-head-gray;
  }
  &:focus,
  &:focus-within {
    @apply border-dark;
    @apply text-neutral;
  }
}
</style>
